import React, { useContext, useState, useEffect, useRef } from "react";
import styled from "styled-components/native";
import {
  Alert,
  Linking,
  Platform,
  TouchableOpacity,
  Image,
} from "react-native";
import {
  Typography,
  Divider,
  Button,
  OldButton,
  Stack,
  TextInput,
  ModalContent,
  Box,
  Spacer,
  Toast,
  CheckBox,
} from "digiwash-components";
import { formatPrice, formatNumber } from "digiwash-core";
import { radii, color } from "@sloth-system/styled-utils";
import Modal from "../ReactNativeModal";

import { CreditContext } from "../../contexts/credit";
import useAddCredit from "../../hooks/useAddCredit";
import { EVENT_TYPES, logEvent } from "../../helpers/analytics-helper";
import useProfile from "../../ducks/user/hooks/useProfile";
import useScrollLock from "../../hooks/useScrollLock";
import CustomIcon from "../CustomIcon";
import { usePayGateways } from "../../hooks/useGateways";

let UserInfoInput = styled(TextInput)`
  background-color: ${color("neutrals.200")};
  border: 0;
  border-radius: ${radii("xm")};
`;

function ModalCredit(props) {
  let { data } = useProfile();
  let { isCreditModalOpen, setCreditModalOpen, prefilledValue, meta } =
    useContext(CreditContext);
  let [amount, setAmount] = useState(null);
  let { loading, addCredit } = useAddCredit();
  let { enable, disable } = useScrollLock();
  let [isEdit, setIsEdit] = useState(false);
  let [selectedGateway, setSelectedGateway] = useState();
  let ref = useRef();
  let { retrieve, paymentGateways } = usePayGateways();

  let canSubmitForm = !!amount && !loading;
  let onSubmit = (selectedId) => {
    if (canSubmitForm) {
      logEvent(EVENT_TYPES.BUTTON_ADD_CREDIT);

      addCredit({
        amount: amount * 10,
        origin: meta.origin,
        address_id: meta.address_id,
        period_id: meta.period_id,
        order_id: meta.order_id,
        gatewayId: selectedId,
      })
        .then((res) => {
          setCreditModalOpen(false);
          Linking.openURL(res.psp_payment_url);
        })
        .catch((e) => {
          ref.current.show(e, {
            data: { type: "danger" },
          });
        });
    }
  };
  let dismiss = () => {
    setCreditModalOpen(false);
    setIsEdit(false);
  };
  let onChangeAmount = (amount) => {
    amount = formatNumber(amount, "en");
    setAmount(amount);
  };

  useEffect(() => {
    setAmount(prefilledValue);
  }, [prefilledValue]);

  useEffect(() => {
    if (isCreditModalOpen) {
      retrieve();
      enable();
    } else {
      disable();
    }
  }, [isCreditModalOpen]);

  useEffect(() => {
    if (paymentGateways) {
      setSelectedGateway(paymentGateways?.data?.paymentTypes?.payments[0]?.id);
    }
  }, [paymentGateways]);

  const confirmationAlert = () => {
    if (Platform.OS === "web") {
      let confirmed = confirm(
        "آیا از انصراف خود برای ویرایش مبلغ اطمینان دارید؟",
      );
      if (confirmed) {
        setIsEdit(false);
      }
    } else {
      Alert.alert(
        "انصراف",
        "آیا از انصراف خود برای ویرایش مبلغ اطمینان دارید؟",
        [
          {
            text: "خیر",
            style: "cancel",
          },
          {
            text: "بله",
            onPress: () => {
              setIsEdit(false);
            },
          },
        ],
        { cancelable: false }
      );
    }
  };

  return (
    <Modal
      isVisible={isCreditModalOpen}
      onSwipeComplete={dismiss}
      onBackdropPress={dismiss}
      swipeDirection={["down"]}
      style={{
        justifyContent: "flex-end",
        margin: "auto",
        width: "100%",
        alignItems: "center",
      }}
    >
      <Box
        bg="common.white"
        style={{
          width: "100%",
          maxWidth: 768,
          borderTopRightRadius: 12,
          borderTopLeftRadius: 12,
        }}
        pt="l"
      >
        <Box px="l">
          {data?.total_balance ? (
            <Stack distribution="space-between">
              <Typography fs="xm" color="common.black" ff="mainMedium">
                اعتبار فعلی:
              </Typography>
              <Stack>
                <Typography fs="xm" color="common.black" ff="mainMedium">
                  {formatPrice(data.total_balance)}
                </Typography>
                <Spacer size="s" />
                <Typography fs="xm" color="common.black" ff="mainLight">
                  تومان
                </Typography>
              </Stack>
            </Stack>
          ) : null}
          {!prefilledValue ? (
            <>
              <Divider size="l" />
              <Stack distribution="space-between">
                <OldButton
                  bg={amount == 100000 ? "primary.main" : "primary.light940"}
                  onPress={() => {
                    onChangeAmount(100000);
                  }}
                  py="s"
                  px="xxxl"
                  style={{ borderRadius: 12 }}
                >
                  <Stack distribution="center" direction="vertical">
                    <Typography
                      fs="m"
                      ff="mainBold"
                      color={amount == 100000 ? "common.white" : "common.black"}
                    >
                      {formatPrice(100000, false)}
                    </Typography>
                    <Typography
                      fs="m"
                      ff="mainLight"
                      color={amount == 100000 ? "common.white" : "common.black"}
                    >
                      تومان
                    </Typography>
                  </Stack>
                </OldButton>
                <OldButton
                  bg={amount == 150000 ? "primary.main" : "primary.light940"}
                  onPress={() => {
                    onChangeAmount(150000);
                  }}
                  py="s"
                  px="xxxl"
                  style={{ borderRadius: 12 }}
                >
                  <Stack distribution="center" direction="vertical">
                    <Typography
                      fs="m"
                      ff="mainBold"
                      color={amount == 150000 ? "common.white" : "common.black"}
                    >
                      {formatPrice(150000, false)}
                    </Typography>
                    <Typography
                      fs="m"
                      ff="mainLight"
                      color={amount == 150000 ? "common.white" : "common.black"}
                    >
                      تومان
                    </Typography>
                  </Stack>
                </OldButton>
                <OldButton
                  bg={amount == 200000 ? "primary.main" : "primary.light940"}
                  onPress={() => {
                    onChangeAmount(200000);
                  }}
                  py="s"
                  px="xxxl"
                  style={{ borderRadius: 12 }}
                >
                  <Stack distribution="center" direction="vertical">
                    <Typography
                      fs="m"
                      ff="mainBold"
                      color={amount == 200000 ? "common.white" : "common.black"}
                    >
                      {formatPrice(200000, false)}
                    </Typography>
                    <Typography
                      fs="m"
                      ff="mainLight"
                      color={amount == 200000 ? "common.white" : "common.black"}
                    >
                      تومان
                    </Typography>
                  </Stack>
                </OldButton>
              </Stack>
              <Divider size="l" />
              <Typography fs="m" color="common.black" ff="mainLight">
                مبلغ مورد نظر خود را وارد کنید.
              </Typography>
              <Divider size="l" />
              <Stack>
                <Box style={{ flexGrow: 1 }}>
                  <UserInfoInput
                    onChangeText={onChangeAmount}
                    value={amount && `${amount}`}
                    keyboardType="numeric"
                    returnKeyType="send"
                    placeholder={`مبلغ دلخواه`}
                    onSubmitEditing={onSubmit}
                  />
                </Box>
                <Spacer size="m" />
                <Typography fs="m" ff="mainLight" color={"common.black"}>
                  تومان
                </Typography>
              </Stack>
              <Divider size="l" />
            </>
          ) : (
            <>
              <Divider size="l" />
              {isEdit ? (
                <>
                  <Typography fs="m" color="common.black" ff="mainLight">
                    مبلغ مورد نظر خود را وارد کنید.
                  </Typography>
                  <Divider size="l" />
                  <Stack>
                    <Box style={{ flexGrow: 1 }}>
                      <UserInfoInput
                        onChangeText={onChangeAmount}
                        keyboardType="numeric"
                        returnKeyType="send"
                        placeholder={`مبلغ دلخواه`}
                        onSubmitEditing={onSubmit}
                      />
                    </Box>
                    <Spacer size="m" />
                    <Typography fs="m" ff="mainLight" color={"common.black"}>
                      تومان
                    </Typography>
                  </Stack>
                </>
              ) : (
                <Stack>
                  <Box style={{ flexGrow: 1, position: "relative" }}>
                    <Box bg="primary.light920" px="l" py="xm" br="l">
                      <Stack>
                        <Typography color="gray.light950">
                          قابل پرداخت:
                        </Typography>
                        <Typography color="common.black" ff="mainBold">
                          {" "}
                          {formatPrice(amount, false)} تومان
                        </Typography>
                      </Stack>
                    </Box>
                    {!props.isGateways && (
                      <Stack
                        style={{
                          position: "absolute",
                          height: "100%",
                          left: 16,
                        }}
                      >
                        <TouchableOpacity
                          onPress={() => {
                            setIsEdit(true);
                          }}
                        >
                          <CustomIcon icon="editPrice" width={24} height={24} />
                        </TouchableOpacity>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              )}
            </>
          )}

          <Divider size="l" />
          {props.isGateways && (
            <>
              <Box>
                <Typography color="common.black" ff="mainBold">
                  انتخاب درگاه
                </Typography>
                <Box>
                  {paymentGateways?.data?.paymentTypes.payments.map((item) => {
                    let isSnapppayDisabled =
                      props.hasShopService() &&
                      item.id === "d6df18cbd82b2ec7e4d2cfc4f509ab91";
                    return (
                      <TouchableOpacity
                        style={{ marginBottom: 16 }}
                        onPress={() => {
                          setSelectedGateway(item.id);
                        }}
                        disabled={isSnapppayDisabled}
                      >
                        <Stack>
                          {isSnapppayDisabled ? null : (
                            <CheckBox
                              width={12}
                              height={12}
                              selected={selectedGateway === item.id}
                            />
                          )}

                          <Image
                            resizeMode="contain"
                            source={{ uri: `${item.logo}` }}
                            style={{ width: 40, height: 40, marginRight: 8 }}
                          />
                          <Typography
                            style={{ marginRight: 8 }}
                            fs="s"
                            ff="mainBold"
                          >
                            {isSnapppayDisabled
                              ? "این درگاه برای خرید محصولات فروشگاهی غیرفعال می‌باشد."
                              : item.title}
                          </Typography>
                        </Stack>
                      </TouchableOpacity>
                    );
                  })}
                </Box>
              </Box>
              <Divider size="l" />
            </>
          )}
          <Button
            outline
            size="m"
            onPress={() => onSubmit(selectedGateway)}
            disabled={!canSubmitForm || !selectedGateway}
          >
            تایید مبلغ و پرداخت
          </Button>
          {isEdit ? (
            <>
              <Divider size="xm" />
              <Stack distribution="center">
                <Button
                  fill="transparent"
                  textColor="red.main"
                  size="m"
                  onPress={confirmationAlert}
                >
                  انصراف
                </Button>
              </Stack>
            </>
          ) : null}
          <Divider size="l" />
        </Box>
      </Box>
      <Toast ref={ref} />
    </Modal>
  );
}

export default ModalCredit;
